// Category pages (/shop/<category>... and their /filters/ mirror) are the only
// pages that encode filters as url path segments. Everywhere else keeps filters
// in query params, so those pages have no [filters] route and any extra path
// segment is invalid — bots were inventing them, e.g.
// /shop/all/tm-racing-kemi-moto-...-used-seven.
//
// No imports on purpose: this runs in middleware, so it has to stay edge-safe.

/** /shop children that are their own page rather than a category slug. */
const NON_CATEGORY_SHOP_SECTIONS = new Set([
  'all',
  'auctions',
  'curated',
  'featured',
  'recent',
  'sale',
]);

/** Segments a valid url ends at; '*' matches any single segment. */
const QUERY_FILTER_ROUTES = [
  ['search'],
  ['filters', 'search'],
  ['brands', '*'],
  ['profile', '*'],
  ['shop', 'all'],
  ['shop', 'auctions'],
  ['shop', 'curated', '*'],
  ['shop', 'featured'],
  ['shop', 'recent'],
  ['shop', 'sale'],
];

const toSegments = (pathname: string) => pathname.split('/').filter(Boolean);

/**
 * True for category pages, the only ones that can build a filtered url path
 * (they have a current_cat to anchor the slug to). Use this before linking to
 * a filtered path — anywhere else the filter belongs in a query param.
 */
export function usesPathFilters(pathname: string) {
  const segments = toSegments(pathname);
  const shop = segments[0] === 'filters' ? segments.slice(1) : segments;
  if (shop[0] !== 'shop' || shop.length < 2) return false;
  return !NON_CATEGORY_SHOP_SECTIONS.has(shop[1]);
}

/**
 * True when a path digs deeper than the page it lands on allows, i.e. a made up
 * [filters] segment. The /shop/* ones would otherwise be swallowed by
 * /shop/[...categorySlug], which does still parse filters from the path.
 */
export function hasInventedPathFilters(pathname: string) {
  const segments = toSegments(pathname);
  return QUERY_FILTER_ROUTES.some(
    (route) =>
      route.length < segments.length &&
      route.every((part, i) => part === '*' || part === segments[i])
  );
}
